import * as i0 from '@angular/core';
import { EventEmitter, Directive, Output, HostListener, InjectionToken, Injectable, Inject, Pipe, NgModule } from '@angular/core';
import { saveAs } from 'file-saver';
import { of, asyncScheduler, BehaviorSubject } from 'rxjs';
import { map, scan, publish, refCount } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { HttpEventType, HttpHeaders } from '@angular/common/http';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { stringify, parse } from 'flatted';
import * as i1$1 from '@angular/platform-browser';

// Usage: <div libCoreClickOutside (clickOutsideEvent)="doSomething()"></div>
class ClickOutsideDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.clickOutsideEvent = new EventEmitter();
    }
    // The document prefix is to consider clicks anywhere in the whole page
    onClickEvent(event, targetElement) {
        if (!targetElement) {
            return;
        }
        const clickedInside = this.elementRef.nativeElement.contains(targetElement);
        if (!clickedInside) {
            // If the click was not inside the element then an event is emitted
            this.clickOutsideEvent.emit(event);
        }
    }
    static { this.ɵfac = function ClickOutsideDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ClickOutsideDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: ClickOutsideDirective, selectors: [["", "libCoreClickOutside", ""]], hostBindings: function ClickOutsideDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("touchstart", function ClickOutsideDirective_touchstart_HostBindingHandler($event) { return ctx.onClickEvent($event, $event.target); }, false, i0.ɵɵresolveDocument)("click", function ClickOutsideDirective_click_HostBindingHandler($event) { return ctx.onClickEvent($event, $event.target); }, false, i0.ɵɵresolveDocument);
        } }, outputs: { clickOutsideEvent: "clickOutsideEvent" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ClickOutsideDirective, [{
        type: Directive,
        args: [{
                selector: '[libCoreClickOutside]'
            }]
    }], () => [{ type: i0.ElementRef }], { clickOutsideEvent: [{
            type: Output
        }], onClickEvent: [{
            type: HostListener,
            args: ['document:touchstart', ['$event', '$event.target']]
        }, {
            type: HostListener,
            args: ['document:click', ['$event', '$event.target']]
        }] }); })();

const SAVER = new InjectionToken('saver');
function getSaver() {
    return saveAs;
}

function isHttpResponse(event) {
    return event.type === HttpEventType.Response;
}
function isHttpProgressEvent(event) {
    return (event.type === HttpEventType.DownloadProgress
        || event.type === HttpEventType.UploadProgress);
}

class ProgressTask {
    constructor(total, loaded, body) {
        this.loaded = loaded;
        this.total = total;
        if (body) {
            this.body = body;
        }
    }
    taskIsComplete() {
        return this.total > 0 && this.total === this.loaded;
    }
}

var PROGRESS_BAR_STATE;
(function (PROGRESS_BAR_STATE) {
    PROGRESS_BAR_STATE["PENDING"] = "PENDING";
    PROGRESS_BAR_STATE["IN_PROGRESS"] = "IN_PROGRESS";
    PROGRESS_BAR_STATE["DONE"] = "DONE";
})(PROGRESS_BAR_STATE || (PROGRESS_BAR_STATE = {}));
var PROGRESS_BAR_MODE;
(function (PROGRESS_BAR_MODE) {
    PROGRESS_BAR_MODE["DETERMINATE"] = "determinate";
    PROGRESS_BAR_MODE["INDETERMINATE"] = "indeterminate";
    PROGRESS_BAR_MODE["BUFFER"] = "buffer";
    PROGRESS_BAR_MODE["QUERY"] = "query";
})(PROGRESS_BAR_MODE || (PROGRESS_BAR_MODE = {}));
class DownloadService {
    constructor(httpClient, save) {
        this.httpClient = httpClient;
        this.save = save;
    }
    downloadUrl(url, fileName) {
        this.getUrlAsBlob(url)
            .subscribe((blob) => {
            this.createAndClickDownlowdLink(blob, fileName);
        }, (error) => {
            console.error(error);
            throw new Error('The file ' + fileName + ' with the url ' + url + ' could not be downloaded.');
        });
    }
    createAndClickDownlowdLink(blob, fileName) {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(objectUrl);
    }
    downloadData(data, fileName) {
        this.getDataAsBlob(data)
            .subscribe((blob) => {
            this.createAndClickDownlowdLink(blob, fileName);
        }, (error) => {
            console.error(error);
            throw new Error('The file ' + fileName + ' could not be downloaded.');
        });
    }
    getDataAsBlob(data) {
        const CONTENT_TYPE = 'application/octet-stream';
        return of(new Blob([data], { type: CONTENT_TYPE }), asyncScheduler);
    }
    getUrlAsBlob(url) {
        return this.httpClient.get(url, {
            responseType: 'blob'
        });
    }
    downloadObservableDataAsBlobWithProgressAndSaveInFile(progressTask$, fileName) {
        return progressTask$.pipe(this.rxjsBodyAsBlob(), this.rxjsDownload((blob) => {
            this.save(blob, fileName);
        }));
    }
    rxjsBodyAsBlob() {
        return function (source) {
            return source.pipe(map((progressTask) => {
                if (!progressTask.taskIsComplete()) {
                    const blobTaskProgress = new ProgressTask(progressTask.total, progressTask.loaded, undefined);
                    return blobTaskProgress;
                }
                else {
                    const CONTENT_TYPE = 'application/octet-stream';
                    const body = new Blob([progressTask.body], { type: CONTENT_TYPE });
                    const blobTaskProgress = new ProgressTask(progressTask.total, progressTask.loaded, body);
                    return blobTaskProgress;
                }
            }));
        };
    }
    rxjsDownload(saver) {
        return function (source) {
            return source.pipe(scan((previous, progressTask) => {
                if (!progressTask.taskIsComplete()) {
                    return {
                        progress: progressTask.total ? Math.round((100 * progressTask.loaded) / progressTask.total) : previous.progress,
                        state: PROGRESS_BAR_STATE.IN_PROGRESS,
                        content: null
                    };
                }
                else {
                    if (saver && progressTask.body) {
                        saver(progressTask.body);
                    }
                    return {
                        progress: 100,
                        state: PROGRESS_BAR_STATE.DONE,
                        content: progressTask.body
                    };
                }
            }, {
                progress: 0,
                state: PROGRESS_BAR_STATE.PENDING,
                content: null
            }));
        };
    }
    statePending(state) {
        return state == PROGRESS_BAR_STATE.PENDING ? true : false;
    }
    stateInProgress(state) {
        return state == PROGRESS_BAR_STATE.IN_PROGRESS ? true : false;
    }
    stateDone(state) {
        return state == PROGRESS_BAR_STATE.DONE ? true : false;
    }
    createProgressTask(total, loaded, body) {
        if (body) {
            return new ProgressTask(total, loaded, body);
        }
        else {
            return new ProgressTask(total, loaded);
        }
    }
    downloadUrlAsBlobWithProgressAndSaveInFile(url, fileName) {
        return this.httpClient.get(url, {
            reportProgress: true,
            observe: 'events',
            responseType: 'blob'
        }).pipe(this.rxjsUrlDownload((blob) => {
            this.save(blob, fileName);
        }));
    }
    // The RxJS custom operator returns a function that takes an observable as parameter and that returns another observable
    // The operator also happen to take an optional parameter which is a function that is
    // then used to persist the downloaded content in to a file without coupling the operator to the persistance implementation
    rxjsUrlDownload(saver) {
        return function (source) {
            return source.pipe(
            // The scan operator can accumulate state by receiving the previous returned value of the custom observable
            scan((previous, event) => {
                if (isHttpProgressEvent(event)) {
                    return {
                        // Progress is the number of loaded bytes to the total number of bytes
                        progress: event.total ? Math.round((100 * event.loaded) / event.total) : previous.progress,
                        state: PROGRESS_BAR_STATE.IN_PROGRESS,
                        content: null
                    };
                }
                if (isHttpResponse(event)) {
                    // If a saver function is passed in then call it to persist the downloaded content
                    if (saver && event.body) {
                        saver(event.body);
                    }
                    return {
                        progress: 100,
                        state: PROGRESS_BAR_STATE.DONE,
                        content: event.body
                    };
                }
                return previous;
            }, 
            // Seed the first call to scan with a previous object representing the initial state of the downloading
            {
                progress: 0,
                state: PROGRESS_BAR_STATE.PENDING,
                content: null
            }));
        };
    }
    static { this.ɵfac = function DownloadService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || DownloadService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(SAVER)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: DownloadService, factory: DownloadService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DownloadService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], () => [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [SAVER]
            }] }], null); })();

class DownloadProgressMatStatePipe {
    constructor(downloadService) {
        this.downloadService = downloadService;
    }
    transform(download) {
        if (download) {
            return this.downloadService.statePending(download.state) ? PROGRESS_BAR_MODE.BUFFER : PROGRESS_BAR_MODE.DETERMINATE;
        }
        else {
            return PROGRESS_BAR_MODE.INDETERMINATE;
        }
    }
    static { this.ɵfac = function DownloadProgressMatStatePipe_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || DownloadProgressMatStatePipe)(i0.ɵɵdirectiveInject(DownloadService, 16)); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "libCoreDownloadProgressMatState", type: DownloadProgressMatStatePipe, pure: true }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DownloadProgressMatStatePipe, [{
        type: Pipe,
        args: [{ name: 'libCoreDownloadProgressMatState' }]
    }], () => [{ type: DownloadService }], null); })();
class DownloadProgressMatProgressPipe {
    transform(download) {
        if (download) {
            return download.progress;
        }
        else {
            return 0;
        }
    }
    static { this.ɵfac = function DownloadProgressMatProgressPipe_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || DownloadProgressMatProgressPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "libCoreDownloadProgressMatProgress", type: DownloadProgressMatProgressPipe, pure: true }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DownloadProgressMatProgressPipe, [{
        type: Pipe,
        args: [{ name: 'libCoreDownloadProgressMatProgress' }]
    }], null, null); })();

class DownloadModule {
    static { this.ɵfac = function DownloadModule_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || DownloadModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: DownloadModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
            {
                provide: SAVER,
                useFactory: getSaver
            }
        ] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DownloadModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    DownloadProgressMatStatePipe,
                    DownloadProgressMatProgressPipe
                ],
                exports: [
                    DownloadProgressMatStatePipe,
                    DownloadProgressMatProgressPipe
                ],
                providers: [
                    {
                        provide: SAVER,
                        useFactory: getSaver
                    }
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(DownloadModule, { declarations: [DownloadProgressMatStatePipe,
        DownloadProgressMatProgressPipe], exports: [DownloadProgressMatStatePipe,
        DownloadProgressMatProgressPipe] }); })();

class LibCoreModule {
    static { this.ɵfac = function LibCoreModule_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || LibCoreModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: LibCoreModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [DownloadModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LibCoreModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    ClickOutsideDirective
                ],
                imports: [
                    DownloadModule
                ],
                exports: [
                    ClickOutsideDirective,
                    DownloadProgressMatStatePipe,
                    DownloadProgressMatProgressPipe
                ],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(LibCoreModule, { declarations: [ClickOutsideDirective], imports: [DownloadModule], exports: [ClickOutsideDirective,
        DownloadProgressMatStatePipe,
        DownloadProgressMatProgressPipe] }); })();

class CommonService {
    is(value) {
        return coerceBooleanProperty(value);
    }
    isSet(value) {
        return value != undefined && value != null;
    }
    isArray(value) {
        return Array.isArray(value);
    }
    isObject(value) {
        return typeof value === 'object' && value !== null;
    }
    isNumber(value) {
        return typeof value === 'number';
    }
    normalizeName(name) {
        return name ? name.replace(/\s/g, '') : '';
    }
    getRandomIntegerBetweenAndExcept(min, max, except) {
        let random;
        do {
            random = this.getRandomIntegerBetween(min, max);
        } while (except.includes(random));
        return random;
    }
    getRandomIntegerBetween(min, max) {
        return Math.floor(Math.random() * max) + min;
    }
    getRandomString(length) {
        const start = 2;
        return Math.random().toString(36).substring(start, start + length);
    }
    sleep(milliseconds) {
        const date = Date.now();
        let currentDate = null;
        do {
            currentDate = Date.now();
        } while (currentDate - date < milliseconds);
    }
    static { this.ɵfac = function CommonService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || CommonService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: CommonService, factory: CommonService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

// The options need to be explicitly typed so as to have the typescript compiler use the correct get() method,
// the get method that returns the interface <T>> and not the one that returns an HttpEvent<T> of the interface.
class HttpOptions {
}
class HttpService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    get(url, httpParams, headers) {
        let options = this.buildOptions(headers);
        options = this.addOptionParams(options, httpParams);
        return this.httpClient.get(url, options);
    }
    post(url, body, headers) {
        return this.httpClient.post(url, body, this.buildOptions(headers));
    }
    postWithHeadersInResponse(url, body, headers) {
        let options = this.buildOptions(headers);
        options = this.addOptionForFullHttpResponse(options);
        return this.httpClient.post(url, body, options);
    }
    put(url, body, headers) {
        return this.httpClient.put(url, body, this.buildOptions(headers));
    }
    patch(url, body, headers) {
        return this.httpClient.patch(url, body, this.buildOptions(headers));
    }
    delete(url) {
        return this.httpClient.delete(url, this.buildOptions());
    }
    buildOptions(headers) {
        const options = {
            headers: this.buildHeader(headers),
            responseType: 'json'
        };
        return options;
    }
    buildHeader(headers) {
        headers = headers || new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json');
        headers = headers.set('Accept', 'application/json');
        return headers;
    }
    addOptionParams(options, httpParams) {
        options['params'] = httpParams;
        return options;
    }
    // Return a full HTTP response with its headers instead of simply the response body
    addOptionForFullHttpResponse(options) {
        options['observe'] = 'response';
        return options;
    }
    static { this.ɵfac = function HttpService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || HttpService)(i0.ɵɵinject(i1.HttpClient)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: HttpService, factory: HttpService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HttpService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], () => [{ type: i1.HttpClient }], null); })();

class LocalStorageService {
    stringify(value) {
        return stringify(value);
    }
    parse(value) {
        try {
            return parse(value);
        }
        catch (e) {
            return null;
        }
    }
    set(key, item) {
        localStorage.setItem(key, this.stringify(item));
    }
    get(key) {
        const value = localStorage.getItem(key);
        return value ? this.parse(value) : null;
    }
    getAll(prefix) {
        const items = new Array();
        if (localStorage) {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.includes(prefix)) {
                    const value = this.get(key);
                    if (value) {
                        items.push(value);
                    }
                }
            }
        }
        return items;
    }
    delete(key) {
        localStorage.removeItem(key);
    }
    deleteAll() {
        localStorage.clear();
    }
    hasLocalStorage() {
        return window.localStorage != null;
    }
    isEmpty() {
        return localStorage ? localStorage.length === 0 : true;
    }
}

// TODO https://blog.angular-university.io/angular-push-notifications/
// Redo this service as seen in the above blog article
class NotificationService {
    constructor() {
        this._notification = new BehaviorSubject('');
        this.notification$ = this._notification.asObservable().pipe(publish(refCount()));
    }
    notify(message) {
        this._notification.next(message);
        setTimeout(() => this._notification.next(''), 3000);
    }
    static { this.ɵfac = function NotificationService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || NotificationService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: NotificationService, factory: NotificationService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NotificationService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], () => [], null); })();

class ScreenDeviceService {
    constructor(meta, title) {
        this.meta = meta;
        this.title = title;
    }
    getDeviceWindowRef() {
        return window;
    }
    getScreenInnerWidth() {
        return this.getDeviceWindowRef().innerWidth;
    }
    getScreenOuterWidth() {
        return this.getDeviceWindowRef().outerWidth;
    }
    getScreenWidth() {
        return this.getDeviceWindowRef().width;
    }
    getScreenHeight() {
        return this.getDeviceWindowRef().innerWidth;
    }
    setMetaData(title, description, color, image) {
        this.title.setTitle(title);
        const tags = [
            { name: 'description', content: description },
            { name: 'theme-color', content: color },
            { name: 'twitter:card', content: 'summary' },
            { name: 'twitter:image', content: image },
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: description },
            { name: 'apple-mobile-web-app-capable', content: 'yes' },
            { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
            { name: 'apple-mobile-web-app-title', content: title },
            { name: 'apple-touch-startup-image', content: image },
            { name: 'og:title', content: title },
            { name: 'og:description', content: description },
            { name: 'og:image', content: image },
        ];
        tags.forEach((tag) => this.meta.updateTag(tag));
    }
    reloadPage() {
        window.location.reload();
    }
    showElement(elementRef) {
        elementRef.nativeElement.style.display = 'block';
    }
    hideElement(elementRef) {
        elementRef.nativeElement.style.display = 'none';
    }
    domRemove(element) {
        if (element != null && element.parentElement != null) {
            element.parentElement.removeChild(element);
            element.parentElement.innerHTML = '';
        }
    }
    static { this.ɵfac = function ScreenDeviceService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ScreenDeviceService)(i0.ɵɵinject(i1$1.Meta), i0.ɵɵinject(i1$1.Title)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ScreenDeviceService, factory: ScreenDeviceService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ScreenDeviceService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], () => [{ type: i1$1.Meta }, { type: i1$1.Title }], null); })();

class WakelockService {
    constructor(meta) {
        this.meta = meta;
    }
    ngOnDestroy() {
        let wakeLock = null;
        let navigator = window.navigator;
        if ('wakeLock' in navigator && 'request' in navigator.wakeLock) {
            wakeLock.removeEventListener('release', this.releasedLockListener);
        }
    }
    setEnvironment(environment) {
        this.environment = environment;
    }
    setMetaToken(token) {
        const tags = [
            { httpEquiv: 'origin-trial', content: token }
        ];
        tags.forEach((tag) => this.meta.updateTag(tag));
    }
    requestWakeLock() {
        let wakeLock = null;
        let pageWindow = window;
        let navigator = window.navigator;
        if ('WakeLock' in pageWindow && 'request' in pageWindow.WakeLock) {
            console.warn('The Wake Lock API is supported by the browser window object.');
            const requestWakeLock = () => {
                const controller = new AbortController();
                const signal = controller.signal;
                pageWindow.WakeLock.request('screen', { signal })
                    .catch((error) => {
                    if (error.name === 'AbortError') {
                        console.log('The Wake Lock request has aborted');
                    }
                    else {
                        console.error(`${error.name}, ${error.message}`);
                    }
                });
                console.log('The Wake Lock is active');
                return controller;
            };
            wakeLock = requestWakeLock();
            if (this.environment) {
                this.environment.wakeLock = wakeLock;
            }
            else {
                throw new Error('Set the environment using the setEnvironment setter before requesting a lock');
            }
            console.log('Got the window Wake Lock');
        }
        else if ('wakeLock' in navigator && 'request' in navigator.wakeLock) {
            console.warn('The Wake Lock API is supported by the browser navigator object.');
            const requestWakeLock = async () => {
                try {
                    wakeLock = await navigator.wakeLock.request('screen');
                    wakeLock.addEventListener('release', this.releasedLockListener);
                    console.log('The Wake Lock is active');
                    return wakeLock;
                }
                catch (error) {
                    console.error(error);
                }
            };
            requestWakeLock().then((wakeLock) => {
                this.environment.wakeLock = wakeLock;
                console.log('Got the navigator Wake Lock');
            });
        }
        else {
            console.warn('The Wake Lock API is not supported by the browser.');
        }
    }
    releasedLockListener(event) {
        console.log('The Wake Lock has been released');
    }
    releaseWakeLock() {
        let pageWindow = window;
        let navigator = window.navigator;
        let wakeLock = this.environment.wakeLock;
        if (wakeLock != null) {
            if ('WakeLock' in pageWindow && 'request' in pageWindow.WakeLock) {
                wakeLock.abort();
                wakeLock = null;
                console.log('Released the window Wake Lock');
            }
            else if ('wakeLock' in navigator && 'request' in navigator.wakeLock) {
                wakeLock.release();
                wakeLock = null;
                console.log('Released the navigator Wake Lock');
            }
        }
        this.environment.wakeLock = null;
    }
    static { this.ɵfac = function WakelockService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || WakelockService)(i0.ɵɵinject(i1$1.Meta)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: WakelockService, factory: WakelockService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WakelockService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], () => [{ type: i1$1.Meta }], null); })();

/*
 * Public API Surface of lib-core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ClickOutsideDirective, CommonService, DownloadProgressMatProgressPipe, DownloadProgressMatStatePipe, DownloadService, HttpService, LibCoreModule, LocalStorageService, NotificationService, PROGRESS_BAR_MODE, PROGRESS_BAR_STATE, ScreenDeviceService, WakelockService };
